import { type FC, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { ArrowOutwardRounded, LogoutRounded } from "@mui/icons-material";
import {
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import { UserAvatar } from "@/admin/shared/ui";
import { PagePaths } from "@/admin/shared/constants";
import { getUserDisplayName } from "@/admin/shared/utils";
import type { UserType } from "@/types/graphql/admin";

export interface AccountMenuProps {
  user: UserType;
  onSignOut: () => void;
  loading: boolean;
}

export const AccountMenu: FC<AccountMenuProps> = ({
  user,
  onSignOut,
  loading,
}) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Профиль админа открывается диалогом на списке пользователей —
  // ровно так же, как из UserCard в сайдбаре
  const profilePath = `${PagePaths.users}?tab=details&id=${user.id}`;
  const displayName = getUserDisplayName(user);

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleGoToProfile = () => {
    navigate(profilePath);
    handleCloseMenu();
  };

  return (
    <>
      <Tooltip title={`Аккаунт: ${displayName}`}>
        <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
          <UserAvatar
            userId={user.id}
            avatarUrl={user.avatar?.url}
            size={32}
            sx={{ bgcolor: "primary.main" }}
          />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={handleCloseMenu}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem
          component={Link}
          to={profilePath}
          onClick={handleCloseMenu}
          sx={{ display: "flex", gap: 2 }}
        >
          <UserAvatar
            userId={user.id}
            avatarUrl={user.avatar?.url}
            size={32}
            sx={{ bgcolor: "primary.main" }}
          />
          <Stack>
            <Typography color="textPrimary">{displayName}</Typography>
            <Typography color="textSecondary">{user.email}</Typography>
          </Stack>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleGoToProfile}>
          <ListItemIcon
            sx={({ palette }) => ({ color: palette.text.secondary })}
          >
            <ArrowOutwardRounded fontSize="small" />
          </ListItemIcon>
          Перейти в профиль
        </MenuItem>

        <Divider />

        <MenuItem
          onClick={onSignOut}
          sx={({ palette }) => ({ color: palette.error.main })}
          disabled={loading}
        >
          <ListItemIcon>
            {loading ? (
              <CircularProgress size={20} />
            ) : (
              <LogoutRounded fontSize="small" color="error" />
            )}
          </ListItemIcon>
          Выйти
        </MenuItem>
      </Menu>
    </>
  );
};
